import '@livekit/components-styles';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import { DisconnectReason, Room, RoomEvent } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonText, Text, VStack,
} from '@gluestack-ui/themed';

import {
  LiveKitConnectFailureProvider,
  useLiveKitConnectFailureSignal,
} from '@/common/hooks/oralExam/useLiveKitConnectWatchdog';
import log from '@/common/utils/logger';
import { isRefusedLiveKitToken } from '@/common/utils/liveKitReconnectRefusal';
import { OralExamStateDoorbell } from '@/common/components/oralExam/OralExamStateDoorbell';

import { MeetingConfirmProvider } from './MeetingConfirm';
import { MeetingConnectionGuard } from './MeetingConnectionGuard';
import {
  useMeetingLeaveAction,
  type MeetingLeaveOptions,
} from './MeetingControlBar';
import { ORAL_EXAM_ROOM_OPTIONS } from './liveKitRoomOptions';
import { MeetingRoomContext } from './MeetingRoomContext';
import type { MeetingJoinSettings, MeetingRoomRole } from './types';
import { defaultMeetingJoinSettings } from './types';
import { useMeetingOpenElsewhereOptional } from './MeetingOpenElsewhere';
import { classifyPublishError } from './publishError';
import {
  disconnectReasonName,
  liveKitDisconnectAction,
} from './livekitDisconnectObservability';

/** After a rejected room.connect: immediate, then 1s, 2s, 4s, then every 5s. */
const RECONNECT_DELAYS_MS = [0, 1000, 2000, 4000, 5000] as const;

/** One Sentry error per outage after this much reconnect retry time. */
const RECONNECT_OUTAGE_ERROR_MS = 30_000;

/** Delays before each remint request after refused tokens within one outage (first is immediate). */
const REMINT_DELAYS_MS = [0, 2000, 5000, 10_000, 20_000] as const;

/** Max automatic remint requests per outage; the next refusal shows connect-failed UI. */
const MAX_REMINT_REQUESTS_PER_OUTAGE = REMINT_DELAYS_MS.length;

function isClientInitiatedDisconnect(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as Error).message === 'Client initiated disconnect';
}

function liveKitFailureMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

export type MeetingRoomProps = {
  token: string;
  serverUrl: string;
  participantRole: MeetingRoomRole;
  children: React.ReactNode;
  joinSettings?: MeetingJoinSettings;
  onLeave?: () => void;
  onRejoin?: () => void;
  onConnected?: () => void;
  expectDisconnect?: boolean;
  /** Breadcrumb label for LiveKit disconnect / reconnect logs (default `meeting`). */
  observabilitySurface?: string;
} & MeetingLeaveOptions;

function logMeetingPublishError(device: 'microphone' | 'camera', error: unknown): void {
  const kind = classifyPublishError(error);
  const message = `meeting: ${device} ${kind}`;
  if (kind === 'publish_failed') {
    const err = error instanceof Error ? error : new Error(String(error));
    log.error(message, err);
  } else {
    log.warn(message);
  }
}

/**
 * Connects to LiveKit with managed track publishing so mute, device swap,
 * and screen share work via standard @livekit/components-react hooks.
 */
type MeetingRoomConnectedChromeProps = {
  children: React.ReactNode;
  intentionalLeaveRef: React.MutableRefObject<boolean>;
  onRejoin?: () => void;
  expectDisconnect: boolean;
  leaveMeeting: () => void;
  skipLeaveConfirm?: boolean;
  beforeLeave?: () => Promise<boolean | void>;
};

function MeetingRoomConnectedChrome({
  children,
  intentionalLeaveRef,
  onRejoin,
  expectDisconnect,
  leaveMeeting,
  skipLeaveConfirm,
  beforeLeave,
}: MeetingRoomConnectedChromeProps) {
  const handleLeave = useMeetingLeaveAction(leaveMeeting, { skipLeaveConfirm, beforeLeave });

  return (
    <MeetingConnectionGuard
      intentionalLeaveRef={intentionalLeaveRef}
      onRejoin={onRejoin}
      onLeave={() => { handleLeave().catch(() => undefined); }}
      expectDisconnect={expectDisconnect}
    >
      {children}
    </MeetingConnectionGuard>
  );
}

function siteHostForPermissionHelp(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return 'this site';
}

function MeetingRoomInner({
  token,
  serverUrl,
  // `participantRole` is reserved for upcoming role-based UI (e.g. lecturer-only
  // controls inside the room). Kept on the prop surface to avoid API churn.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  participantRole,
  children,
  joinSettings = defaultMeetingJoinSettings,
  onLeave,
  onRejoin,
  onConnected,
  expectDisconnect = false,
  observabilitySurface = 'meeting',
  skipLeaveConfirm = false,
  beforeLeave,
}: MeetingRoomProps) {
  const { t } = useTranslation();
  const openElsewhereCtx = useMeetingOpenElsewhereOptional();
  const intentionalLeaveRef = useRef(false);
  const disposedRef = useRef(false);
  const connectedRef = useRef(false);
  const everConnectedRef = useRef(false);
  const reconnectInFlightRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectFailureIndexRef = useRef(0);
  const reconnectOutageStartedAtRef = useRef<number | null>(null);
  const reconnectOutageErrorLoggedRef = useRef(false);
  const reconnectAwaitingRemintRef = useRef(false);
  const remintRefusalCountRef = useRef(0);
  const remintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connectToken, setConnectToken] = useState(token);
  const serverUrlRef = useRef(serverUrl);
  serverUrlRef.current = serverUrl;
  const connectTokenRef = useRef(connectToken);
  connectTokenRef.current = connectToken;
  const expectDisconnectRef = useRef(expectDisconnect);
  expectDisconnectRef.current = expectDisconnect;
  const [micBlocked, setMicBlocked] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [initialMediaSettled, setInitialMediaSettled] = useState(false);
  const siteHost = useMemo(() => siteHostForPermissionHelp(), []);

  // Same-room remints must not feed a new JWT into LiveKitRoom while
  // connected, first-connecting, or auto-reconnecting — that restarts
  // room.connect and start-session's presence probe misses the student.
  // Adopt a remint only after a real drop whose reconnect already failed
  // (Rejoin).
  useEffect(() => {
    if (token === connectToken) return;
    if (connectedRef.current || !everConnectedRef.current || reconnectInFlightRef.current) {
      return;
    }
    reconnectAwaitingRemintRef.current = false;
    setConnectToken(token);
  }, [token, connectToken]);

  const room = useMemo(() => new Room({
    ...ORAL_EXAM_ROOM_OPTIONS,
    audioCaptureDefaults: joinSettings.audioDeviceId
      ? { deviceId: joinSettings.audioDeviceId }
      : undefined,
    videoCaptureDefaults: joinSettings.videoDeviceId
      ? { deviceId: joinSettings.videoDeviceId }
      : undefined,
  }), [joinSettings.audioDeviceId, joinSettings.videoDeviceId]);

  const clearRemintTimer = useCallback(() => {
    if (remintTimerRef.current != null) {
      clearTimeout(remintTimerRef.current);
      remintTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    disposedRef.current = true;
    if (reconnectTimerRef.current != null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    clearRemintTimer();
    room.disconnect(true);
  }, [clearRemintTimer, room]);

  const leaveMeeting = useCallback(() => {
    intentionalLeaveRef.current = true;
    clearRemintTimer();
    room.disconnect(true);
    onLeave?.();
  }, [clearRemintTimer, onLeave, room]);

  const contextValue = useMemo(
    () => ({ leaveMeeting, initialMediaSettled }),
    [leaveMeeting, initialMediaSettled],
  );

  const enableMicrophone = useCallback(async (): Promise<boolean> => {
    if (!joinSettings.microphoneEnabled) {
      await room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
      return true;
    }
    try {
      await room.localParticipant.setMicrophoneEnabled(true);
      return true;
    } catch (e) {
      logMeetingPublishError('microphone', e);
      return false;
    }
  }, [joinSettings.microphoneEnabled, room]);

  const enableCamera = useCallback(async (): Promise<boolean> => {
    if (!joinSettings.cameraEnabled) {
      await room.localParticipant.setCameraEnabled(false).catch(() => {});
      return true;
    }
    try {
      await room.localParticipant.setCameraEnabled(true);
      return true;
    } catch (e) {
      logMeetingPublishError('camera', e);
      return false;
    }
  }, [joinSettings.cameraEnabled, room]);

  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const onRejoinRef = useRef(onRejoin);
  onRejoinRef.current = onRejoin;

  const { markConnectFailed, clearConnectFailed } = useLiveKitConnectFailureSignal();
  const markConnectFailedRef = useRef(markConnectFailed);
  markConnectFailedRef.current = markConnectFailed;
  const clearConnectFailedRef = useRef(clearConnectFailed);
  clearConnectFailedRef.current = clearConnectFailed;

  const handleConnected = useCallback(async () => {
    const hadReconnectFailures = reconnectFailureIndexRef.current > 0
      || reconnectOutageErrorLoggedRef.current;
    const outageStartedAt = reconnectOutageStartedAtRef.current;

    connectedRef.current = true;
    everConnectedRef.current = true;
    reconnectInFlightRef.current = false;
    reconnectFailureIndexRef.current = 0;
    reconnectOutageStartedAtRef.current = null;
    reconnectOutageErrorLoggedRef.current = false;
    reconnectAwaitingRemintRef.current = false;
    remintRefusalCountRef.current = 0;
    clearRemintTimer();

    if (hadReconnectFailures && outageStartedAt != null) {
      const outageMs = Date.now() - outageStartedAt;
      log.warn(`meeting: reconnect recovered after ${outageMs}ms outage`);
    }

    clearConnectFailedRef.current();
    onConnectedRef.current?.();
    await room.startAudio().catch(() => {});
    const micOk = await enableMicrophone();
    const camOk = await enableCamera();
    setMicBlocked(!micOk);
    setCameraBlocked(!camOk);
    setInitialMediaSettled(true);
  }, [clearRemintTimer, enableCamera, enableMicrophone, room]);

  const scheduleRemintAfterRefusal = useCallback(() => {
    if (disposedRef.current || intentionalLeaveRef.current || expectDisconnectRef.current) {
      return;
    }
    if (remintTimerRef.current != null) {
      return;
    }

    remintRefusalCountRef.current += 1;
    const refusalIndex = remintRefusalCountRef.current;

    if (refusalIndex > MAX_REMINT_REQUESTS_PER_OUTAGE) {
      reconnectAwaitingRemintRef.current = true;
      markConnectFailedRef.current();
      return;
    }

    reconnectAwaitingRemintRef.current = true;
    const delayMs = REMINT_DELAYS_MS[refusalIndex - 1];
    if (delayMs === 0) {
      onRejoinRef.current?.();
      return;
    }
    remintTimerRef.current = setTimeout(() => {
      remintTimerRef.current = null;
      if (disposedRef.current || intentionalLeaveRef.current || expectDisconnectRef.current) {
        return;
      }
      onRejoinRef.current?.();
    }, delayMs);
  }, []);

  const handleRetryMedia = useCallback(async () => {
    if (micBlocked) {
      const micOk = await enableMicrophone();
      if (!micOk) {
        return;
      }
      setMicBlocked(false);
    }
    if (cameraBlocked || joinSettings.cameraEnabled) {
      const camOk = await enableCamera();
      if (!camOk) {
        setCameraBlocked(true);
        return;
      }
    }
    setCameraBlocked(false);
    setInitialMediaSettled(true);
  }, [
    cameraBlocked,
    enableCamera,
    enableMicrophone,
    joinSettings.cameraEnabled,
    micBlocked,
  ]);

  const handleConnectedRef = useRef(handleConnected);
  handleConnectedRef.current = handleConnected;

  const reportReconnectFailure = useCallback((error: unknown) => {
    const message = liveKitFailureMessage(error);

    if (isClientInitiatedDisconnect(error)) {
      log.warn(`meeting: reconnect stopped message=${message}`);
      return { refusedToken: false, stopRetries: true };
    }

    log.warn(`meeting: reconnect attempt failed message=${message}`);

    if (reconnectOutageStartedAtRef.current == null) {
      reconnectOutageStartedAtRef.current = Date.now();
    }

    const attemptCount = reconnectFailureIndexRef.current + 1;
    const outageElapsedMs = Date.now() - reconnectOutageStartedAtRef.current;
    const shouldReportOutageError = !reconnectOutageErrorLoggedRef.current
      && (isRefusedLiveKitToken(error) || outageElapsedMs >= RECONNECT_OUTAGE_ERROR_MS);

    if (shouldReportOutageError) {
      reconnectOutageErrorLoggedRef.current = true;
      const err = error instanceof Error ? error : new Error(message);
      log.error(
        `meeting: reconnect outage attempts=${attemptCount} lastMessage=${message}`,
        err,
      );
    }

    if (isRefusedLiveKitToken(error)) {
      scheduleRemintAfterRefusal();
      return { refusedToken: true, stopRetries: true };
    }

    return { refusedToken: false, stopRetries: false };
  }, [scheduleRemintAfterRefusal]);

  const scheduleReconnectAttempt = useCallback((delayMs: number) => {
    if (disposedRef.current || intentionalLeaveRef.current || expectDisconnectRef.current) {
      return;
    }
    if (reconnectTimerRef.current != null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const runAttempt = () => {
      reconnectTimerRef.current = null;
      if (disposedRef.current || intentionalLeaveRef.current || expectDisconnectRef.current) {
        return;
      }
      if (reconnectInFlightRef.current) {
        return;
      }
      const url = serverUrlRef.current;
      const tok = connectTokenRef.current;
      if (!url || !tok) {
        return;
      }
      reconnectInFlightRef.current = true;
      room.connect(url, tok).catch((error: Error) => {
        reconnectInFlightRef.current = false;
        const { refusedToken, stopRetries } = reportReconnectFailure(error);
        if (stopRetries) {
          return;
        }
        if (!refusedToken) {
          markConnectFailedRef.current();
        }
        const failureIndex = reconnectFailureIndexRef.current;
        const cappedIndex = Math.min(failureIndex, RECONNECT_DELAYS_MS.length - 1);
        reconnectFailureIndexRef.current = failureIndex + 1;
        scheduleReconnectAttempt(RECONNECT_DELAYS_MS[cappedIndex]);
      });
    };
    reconnectTimerRef.current = setTimeout(runAttempt, Math.max(0, delayMs));
  }, [reportReconnectFailure, room]);

  // Connect publishes whatever the flags say at that moment. A later flip
  // (briefing camera allowed, or phase poll unpublish on a shared room)
  // must follow. Allow-to-speak turns the microphone on without changing
  // this flag, so only a change in the flag is applied.
  const publishedMicRef = useRef(joinSettings.microphoneEnabled);
  const publishedCameraRef = useRef(joinSettings.cameraEnabled);
  useEffect(() => {
    const micNext = joinSettings.microphoneEnabled;
    const micPrevious = publishedMicRef.current;
    publishedMicRef.current = micNext;

    const cameraNext = joinSettings.cameraEnabled;
    const cameraPrevious = publishedCameraRef.current;
    publishedCameraRef.current = cameraNext;

    if (!connectedRef.current) return;

    if (micPrevious !== micNext) {
      if (micNext) {
        room.localParticipant.setMicrophoneEnabled(true)
          .then(() => {
            setMicBlocked(false);
          })
          .catch((e) => {
            logMeetingPublishError('microphone', e);
            setMicBlocked(true);
          });
      } else {
        room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
      }
    }

    if (cameraPrevious !== cameraNext) {
      if (cameraNext) {
        room.localParticipant.setCameraEnabled(true)
          .then(() => {
            setCameraBlocked(false);
          })
          .catch((e) => {
            logMeetingPublishError('camera', e);
            setCameraBlocked(true);
          });
      } else {
        room.localParticipant.setCameraEnabled(false).catch(() => {});
      }
    }
  }, [joinSettings.microphoneEnabled, joinSettings.cameraEnabled, room]);

  // LiveKitRoom's connect effect depends on onError. A new function identity
  // calls room.connect() again — disconnect first — Connection lost.
  const handleError = useCallback((error: Error) => {
    if (!everConnectedRef.current) {
      log.error('meeting: livekit connect error', error);
      markConnectFailedRef.current();
      return;
    }
    const { stopRetries } = reportReconnectFailure(error);
    if (!stopRetries) {
      markConnectFailedRef.current();
    }
  }, [reportReconnectFailure]);

  useEffect(() => {
    const surface = observabilitySurface;
    const onReconnecting = () => {
      log.warn(`livekit reconnecting surface=${surface}`);
    };
    const onSignalReconnecting = () => {
      log.warn(`livekit signal_reconnecting surface=${surface}`);
    };
    const onReconnected = () => {
      log.warn(`livekit reconnected surface=${surface}`);
    };
    room.on(RoomEvent.Reconnecting, onReconnecting);
    room.on(RoomEvent.SignalReconnecting, onSignalReconnecting);
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnecting, onReconnecting);
      room.off(RoomEvent.SignalReconnecting, onSignalReconnecting);
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [observabilitySurface, room]);

  const handleDisconnected = useCallback((reason?: DisconnectReason) => {
    connectedRef.current = false;
    if (disposedRef.current || intentionalLeaveRef.current || expectDisconnectRef.current) {
      clearRemintTimer();
      return;
    }
    const action = liveKitDisconnectAction(reason, Boolean(openElsewhereCtx));
    log.warn(
      `livekit disconnected reason=${disconnectReasonName(reason)} surface=${observabilitySurface} action=${action}`,
    );
    if (openElsewhereCtx) {
      if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        openElsewhereCtx.reportDuplicateIdentity('meeting-room');
        return;
      }
      if (
        openElsewhereCtx.stopOnRoomClosed
        && (
          reason === DisconnectReason.ROOM_DELETED
          || reason === DisconnectReason.PARTICIPANT_REMOVED
        )
      ) {
        return;
      }
    }
    if (reconnectAwaitingRemintRef.current) {
      return;
    }
    if (reconnectInFlightRef.current) {
      return;
    }
    const ongoingOutage = reconnectOutageStartedAtRef.current != null;
    if (!ongoingOutage) {
      reconnectFailureIndexRef.current = 0;
      reconnectOutageStartedAtRef.current = Date.now();
      reconnectOutageErrorLoggedRef.current = false;
    }
    // useLiveKitRoom's connect effect only re-runs when token/serverUrl/room change.
    // An in-session drop (examiner join, signal reset) leaves deps unchanged, so
    // the SDK stays Disconnected until we connect again explicitly.
    scheduleReconnectAttempt(0);
  }, [clearRemintTimer, observabilitySurface, openElsewhereCtx, scheduleReconnectAttempt]);

  const stableOnConnected = useCallback(() => {
    Promise.resolve(handleConnectedRef.current()).catch(() => undefined);
  }, []);

  const mediaBlocked = micBlocked || cameraBlocked;

  return (
    <Box flex={1} minHeight={0} testID="meeting-room">
      {/* audio/video props deliberately omitted: handleConnected is the single
          owner of initial track state so lobby mute choices are respected.
          The inline style makes the LiveKit <div> fill its flex parent so the
          video stage below inherits a real height instead of collapsing. */}
      <LiveKitRoom
        room={room}
        token={connectToken}
        serverUrl={serverUrl}
        connect
        onConnected={stableOnConnected}
        onDisconnected={handleDisconnected}
        onError={handleError}
        style={{
          display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
        }}
      >
        <RoomAudioRenderer />
        <OralExamStateDoorbell />
        <MeetingRoomContext.Provider value={contextValue}>
          {mediaBlocked ? (
            <Box
              flex={1}
              bg="$bgApp"
              p="$4"
              testID={micBlocked ? 'meeting-mic-blocked' : 'meeting-camera-blocked'}
            >
              <VStack space="md" alignItems="center" justifyContent="center" flex={1} maxWidth={480} alignSelf="center">
                <Text size="lg" fontWeight="semibold" color="$statusError" textAlign="center">
                  {micBlocked ? t('oralExam.micDenied') : t('oralExam.cameraDenied')}
                </Text>
                <Text size="sm" color="$textBody" textAlign="center">
                  {t('oralExam.mediaBothRequired')}
                </Text>
                <VStack space="sm" width="100%">
                  <Text size="sm" color="$textBody" textAlign="center">
                    {t('oralExam.mediaBlockedHelpLead', { host: siteHost })}
                  </Text>
                  <Text size="sm" color="$textSubtle" textAlign="center">
                    {t('oralExam.mediaBlockedHelpChrome', { host: siteHost })}
                  </Text>
                  <Text size="sm" color="$textSubtle" textAlign="center">
                    {t('oralExam.mediaBlockedHelpSafari', { host: siteHost })}
                  </Text>
                </VStack>
                <Button
                  size="sm"
                  onPress={handleRetryMedia}
                  testID="meeting-media-retry"
                >
                  <ButtonText>
                    {micBlocked ? t('oralExam.retryMedia') : t('oralExam.retryCamera')}
                  </ButtonText>
                </Button>
                <Button size="sm" variant="outline" onPress={leaveMeeting}>
                  <ButtonText>{t('oralExam.leaveRoom')}</ButtonText>
                </Button>
              </VStack>
            </Box>
          ) : (
            <MeetingConfirmProvider>
              <MeetingRoomConnectedChrome
                intentionalLeaveRef={intentionalLeaveRef}
                onRejoin={onRejoin}
                expectDisconnect={expectDisconnect}
                leaveMeeting={leaveMeeting}
                skipLeaveConfirm={skipLeaveConfirm}
                beforeLeave={beforeLeave}
              >
                {children}
              </MeetingRoomConnectedChrome>
            </MeetingConfirmProvider>
          )}
        </MeetingRoomContext.Provider>
      </LiveKitRoom>
    </Box>
  );
}

export function MeetingRoom(props: MeetingRoomProps) {
  return (
    <LiveKitConnectFailureProvider>
      <MeetingRoomInner {...props} />
    </LiveKitConnectFailureProvider>
  );
}
